import {
  useNotify,
  useRefresh,
} from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useState } from 'react';
import { Add, Notes, Edit, Delete } from "@mui/icons-material";

const token = JSON.parse(localStorage.getItem('token'));

// Add Note Dialog Component
const AddNoteDialog = ({ open, onClose, callRecordId }) => {
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Direct fetch to the correct endpoint
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/call-records/${callRecordId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          agentId: 'admin', // Get from auth context
          noteType: noteType,
          noteText: noteText.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add note');
      }

      await response.json();
      
      notify('Note added successfully', { type: 'success' });
      refresh();
      onClose();
      setNoteText('');
      setNoteType('general');
    } catch (error) {
      console.error('Error adding note:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Note</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Note Type</InputLabel>
            <Select
              value={noteType}
              label="Note Type"
              onChange={(e) => setNoteType(e.target.value)}
            >
              <MenuItem value="reason">Reason</MenuItem>
              <MenuItem value="followup">Follow-up</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <MuiTextField
            label="Note"
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter note details..."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!noteText.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Adding...' : 'Add Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Edit Note Dialog Component
const EditNoteDialog = ({ open, onClose, note, callRecordId }) => {
  const [noteText, setNoteText] = useState(note?.noteText || '');
  const [noteType, setNoteType] = useState(note?.noteType || 'general');
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/call-records/${callRecordId}/notes/${note.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          noteText: noteText.trim(),
          noteType: noteType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update note');
      }

      await response.json();
      
      notify('Note updated successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      console.error('Error updating note:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Note</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Note Type</InputLabel>
            <Select
              value={noteType}
              label="Note Type"
              onChange={(e) => setNoteType(e.target.value)}
            >
              <MenuItem value="reason">Reason</MenuItem>
              <MenuItem value="followup">Follow-up</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <MuiTextField
            label="Note"
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Enter note details..."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!noteText.trim() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : 'Update Note'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Single Note Item Component
const NoteItem = ({ note, callRecordId }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const getNoteTypeColor = (type) => {
    switch (type) {
      case 'reason': return 'primary';
      case 'followup': return 'warning';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/call-records/${callRecordId}/notes/${note.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete note');
      }

      notify('Note deleted successfully', { type: 'success' });
      refresh();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting note:', error);
      notify(`Error: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={note.noteType}
                  color={getNoteTypeColor(note.noteType)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  by {note.agentId}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => setEditDialogOpen(true)}
                  disabled={loading}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialogOpen(true)}
                  color="error"
                  disabled={loading}
                >
                  Delete
                </Button>
              </Stack>
            </Box>

            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {note.noteText}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {new Date(note.createdAt).toLocaleString()}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <EditNoteDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        note={note}
        callRecordId={callRecordId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Note</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this note? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Main Call Notes Component
export const CallNotes = ({ record }) => {
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const notes = record.notes || [];

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Call Notes ({notes.length})
            </Typography>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => setAddNoteOpen(true)}
            >
              Add Note
            </Button>
          </Box>

          {/* Notes List */}
          {notes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Notes sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                No notes yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add the first note to this call record
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {notes.map((note) => (
                <NoteItem
                  key={note.id}
                  note={note}
                  callRecordId={record.id}
                />
              ))}
            </Stack>
          )}
        </Stack>

        <AddNoteDialog
          open={addNoteOpen}
          onClose={() => setAddNoteOpen(false)}
          callRecordId={record.id}
        />
      </CardContent>
    </Card>
  );
};